import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Reveal, useSeo } from "../lib/ui";
import { HealthCase } from "../components/HealthCase";

export default function HealthAndFitness() {
  useSeo({
    title: "Health and Fitness — KickStartKenyaFootball",
    description:
      "The health, fitness and productivity case for Kenyan grassroots football: fewer hospital admissions, fewer sick days, more output, set against Kenya's own statistics on pneumonia, cancer, cardiovascular disease and rising non-communicable disease.",
  });

  return (
    <div className="bg-black pb-24 pt-12 md:pt-16" data-testid="health-page">
      <section className="px-5 md:px-10">
        <div className="mx-auto max-w-[1500px]">
          <Reveal>
            <p className="eyebrow">Health and fitness</p>
            <h1 className="display-1 mt-5 max-w-4xl">Health and Fitness</h1>
            <p className="body-copy mt-6 max-w-3xl">
              Kenya is treating more preventable illness every year. Pneumonia is the leading registered cause of
              death in the country&rsquo;s health facilities, cancer and cardiovascular disease follow it, and
              non-communicable disease now fills more than half of all hospital beds. Physical inactivity is one of
              the five risk factors the Ministry of Health&rsquo;s own strategy targets. A football programme is a
              health programme: it moves hundreds of thousands of people, screens them where they already gather, and
              gives every county a reason to keep them moving into adulthood.
            </p>
          </Reveal>

          <div className="mt-10 flex flex-wrap gap-4">
            <Link to="/money" className="btn-primary" data-testid="health-money-link">
              The money <ArrowUpRight size={14} />
            </Link>
            <Link to="/facilities" className="btn-ghost" data-testid="health-facilities-link">
              Grassroots ecosystem
            </Link>
            <Link to="/impact" className="btn-ghost" data-testid="health-impact-link">
              Social enterprise and community impact
            </Link>
          </div>

          <HealthCase heading="Fewer people in hospital, fewer days lost, more output" />

          <div className="mt-16 border border-white/10 bg-[#0b0b0b] p-7 md:p-10" data-testid="health-standards">
            <p className="overline text-ochre">Standards we would hold ourselves to</p>
            <div className="mt-6 grid gap-8 md:grid-cols-3">
              {[
                ["Screening at registration", "Height, weight, blood pressure, vision and hearing recorded when a player signs on, and again each season, with referral routes agreed with the county health team before a facility opens."],
                ["A qualified adult on every session", "First aid, concussion protocol, hydration and heat guidance, and safeguarding training for every coach and volunteer — the same standard for girls' sessions as boys'."],
                ["Data the county can use", "Anonymous participation, attendance and screening data shared with county health records, so the health benefit of a pitch can be measured rather than asserted."],
              ].map(([t, b]) => (
                <div key={t}>
                  <p className="font-display text-lg uppercase leading-tight tracking-tight text-white">{t}</p>
                  <p className="mt-3 text-sm leading-relaxed text-zinc-400">{b}</p>
                </div>
              ))}
            </div>
            <p className="mt-8 text-xs leading-relaxed text-zinc-500">
              These are the standards we are proposing, not services running today.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
